import { useEffect, useState } from "react";

type Props = {
  /** Строки-слоганы: [первая строка, вторая строка] */
  slogans: [string, string][];
  className?: string;
  as?: "h1" | "h2" | "div" | "span";
  /** Статичный текст для SSR/поиска (обязателен для h1) */
  staticText?: string;
  /** Что показывать до запуска печати. */
  initialMode?: "full" | "empty";
  /** Когда запускать цикл печати. */
  startMode?: "interaction" | "immediate";
};

/** Эффект печатной машинки: печатает / стирает слоганы по кругу */
export function Typewriter({
  slogans,
  className = "",
  as = "h1",
  staticText,
  initialMode = "empty",
  startMode = "immediate",
}: Props) {
  const firstSloganLength = slogans[0]!.join("\n").length;
  const [line, setLine] = useState(0);
  const [chars, setChars] = useState(() =>
    initialMode === "full" ? firstSloganLength : 0,
  );
  const [deleting, setDeleting] = useState(false);
  const [started, setStarted] = useState(false);

  const current = slogans[line]!;
  const full = current.join("\n");

  useEffect(() => {
    const reducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;
    const reducedData = window.matchMedia(
      "(prefers-reduced-data: reduce)",
    ).matches;

    if (reducedMotion || reducedData) {
      setChars(firstSloganLength);
      return;
    }

    if (startMode === "immediate") {
      setStarted(true);
      return;
    }

    if (started) return;

    const events = ["pointerdown", "touchstart", "keydown", "scroll"] as const;
    const start = () => {
      setStarted(true);
      for (const event of events) window.removeEventListener(event, start);
    };
    for (const event of events) {
      window.addEventListener(event, start, { passive: true, once: true });
    }
    return () => {
      for (const event of events) window.removeEventListener(event, start);
    };
  }, [firstSloganLength, startMode, started]);

  useEffect(() => {
    if (!started) return;

    const interval = window.setInterval(() => {
      setChars((value) => {
        if (deleting) return Math.max(0, value - 1);
        return Math.min(full.length, value + 1);
      });
    }, deleting ? 28 : 16);

    return () => window.clearInterval(interval);
  }, [deleting, full.length, started]);

  useEffect(() => {
    if (!started) return;

    if (!deleting && chars === full.length) {
      const timeout = window.setTimeout(() => setDeleting(true), 2200);
      return () => window.clearTimeout(timeout);
    }

    if (deleting && chars === 0) {
      const timeout = window.setTimeout(() => {
        setDeleting(false);
        setLine((value) => (value + 1) % slogans.length);
      }, 320);
      return () => window.clearTimeout(timeout);
    }

    return undefined;
  }, [chars, deleting, full.length, slogans.length, started]);

  const typed = full.slice(0, chars);
  const [first = "", second = ""] = typed.split("\n");
  const Tag = as;

  const animation = (
    <>
      <span className="flex items-center">
        {first}
        {typed.includes("\n") ? null : (
          <span className="typewriter-caret h-[0.9em]" aria-hidden="true" />
        )}
      </span>
      <span className="flex items-center text-display-muted">
        {second}
        {typed.includes("\n") ? (
          <span className="typewriter-caret h-[0.9em]" aria-hidden="true" />
        ) : null}
      </span>
    </>
  );

  // Со статичным текстом: заголовок содержит только его,
  // анимация живёт в декоративном соседнем блоке.
  if (staticText) {
    return (
      <>
        <Tag className="sr-only">{staticText}</Tag>
        <div aria-hidden="true" className={`typewriter-first-cycle min-h-[2.1em] ${className}`}>
          {animation}
        </div>
      </>
    );
  }

  return (
    <Tag className={`min-h-[2.1em] ${className}`} aria-label={current.join(" ")}>
      {animation}
    </Tag>
  );
}
